import {
  Button,
  CircularPercentage,
  ErrorBoundary,
  Icon,
  Shadow,
  Text,
  HStack,
  VStack,
} from '@genial/components';
import { Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { RouteProp, useRoute } from '@react-navigation/native';
import { Box, Flex } from 'native-base';
import React, { ReactNode, useCallback, useMemo, useState } from 'react';

import { useRecoilQuery } from '../../../../../../hooks/useRecoilQuery';
import { CustomerDetailStackParamList } from '../../../../../../routes/types';
import { customerDetailCompositionPortfolioQuery } from '../../../../../../store/customerDetail/selector';

import { ChartItem } from './components/ChartItem';
import { ChartText } from './components/ChartText';
import { ChartTypeTabs } from './components/ChartTypeTabs';
import { chartTypeTabsByKey, chartTypeTabsInitialTab } from './components/ChartTypeTabs/constants';

const Wrapper: React.FC<{ children: ReactNode }> = ({ children }) => (
  <Shadow level={2} borderRadius="sm" padding="inset.xs" width={{ base: 'full', lg: '2/6' }}>
    {children}
  </Shadow>
);

export interface CompositionChartProps {
  handleGoToFinancialDetails: (selectedTab?: string) => void;
}

export const CompositionChart: React.FC<CompositionChartProps> = ({
  handleGoToFinancialDetails,
}) => {
  const [chartType, setChartType] = useState(chartTypeTabsInitialTab);
  const [viewingAll, setViewingAll] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const route = useRoute<RouteProp<CustomerDetailStackParamList, 'CustomerDetail'>>();
  const { accountNumber } = route.params;
  const { data, loading, error, refresher } = useRecoilQuery(
    customerDetailCompositionPortfolioQuery({ accountNumber })
  );

  const handleSelectChartType = useCallback((selectedChartType: string) => {
    setSelectedIndex(0);
    setChartType(selectedChartType);
  }, []);

  const handleGoToFinancialDetailsWithCurrentTab = useCallback(() => {
    handleGoToFinancialDetails(chartType);
  }, [handleGoToFinancialDetails, chartType]);

  const chartData = useMemo(() => {
    if (!data) {
      return [];
    }

    return data[chartTypeTabsByKey[chartType].key].map((item, index) => ({
      label: item.nameNormalized,
      value: item.value,
      percentage: item.percentage,
      color:
        index === selectedIndex || viewingAll
          ? item.colorNormalized
          : Utils.ColorToken.WithOpacity(item.colorNormalized, 0.5),
      itemColor: item.colorNormalized,
      ignoreValue: item.ignoreValue,
      total: data.total[chartTypeTabsByKey[chartType].totalField],
    }));
  }, [chartType, data, selectedIndex, viewingAll]);

  const handleSelectChartItem = useCallback(
    (index: number) => {
      if (viewingAll) {
        setViewingAll(false);
      } else if (index === selectedIndex) {
        setViewingAll(true);
      }

      setSelectedIndex(index);
    },
    [viewingAll, selectedIndex]
  );

  const chartItems = useMemo(
    () =>
      chartData.map((item, index) => (
        <ChartItem
          key={index.toString()}
          color={item.itemColor}
          label={item.label}
          value={item.value}
          percentage={item.percentage}
          ignoreValue={item.ignoreValue}
          onPress={() => handleSelectChartItem(index)}
          isSelected={viewingAll ? true : index === selectedIndex}
        />
      )),
    [chartData, viewingAll, selectedIndex, handleSelectChartItem]
  );

  const chartComponent = useMemo(() => {
    if (error) {
      return null;
    } else if (loading) {
      return <CircularPercentage loading data={undefined} size={300} />;
    } else if (chartData.length === 0) {
      return (
        <VStack space="stack.xxxs">
          <CircularPercentage
            loading={false}
            data={[
              {
                color: 'neutral.light',
                percentage: 100,
              },
            ]}
            size={300}
            customText={
              <ChartText
                accountNumber={accountNumber}
                baseLabel={chartTypeTabsByKey[chartType].baseLabel}
                label=""
                percentage={100}
                value={0}
              />
            }
          />
          <HStack space="stack.nano" alignItems="center">
            <Icon testId="info-icon-no-assets" name="Info" size="md" color="neutral.medium" />
            <VStack space="stack.nano">
              <Text variant="bold">O Cliente não possui produtos na carteira. </Text>
              <Text>Não há dados suficientes para construir o gráfico.</Text>
            </VStack>
          </HStack>
        </VStack>
      );
    } else {
      const currentLabel = chartData[selectedIndex]?.label ?? '';
      const currentPercentage = chartData[selectedIndex]?.percentage ?? 0;
      const currentValue =
        (viewingAll ? chartData[selectedIndex]?.total : chartData[selectedIndex]?.value) ?? 0;

      return (
        <CircularPercentage
          loading={false}
          data={chartData}
          size={300}
          radiusSize={{ thickness: 5, outer: 150, inner: 140 }}
          onCellClick={handleSelectChartItem}
          customText={
            <ChartText
              accountNumber={accountNumber}
              baseLabel={chartTypeTabsByKey[chartType].baseLabel}
              label={currentLabel}
              hideLabel={viewingAll}
              percentage={viewingAll ? 100 : currentPercentage}
              value={currentValue}
            />
          }
        />
      );
    }
  }, [
    chartData,
    chartType,
    error,
    handleSelectChartItem,
    loading,
    selectedIndex,
    viewingAll,
    accountNumber,
  ]);

  if (error) {
    return (
      <Wrapper>
        <ErrorBoundary onRefresh={refresher} />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <VStack space="stack.xxxs" justifyContent="space-between" flex={1} width="full">
        <ChartTypeTabs onSelectChartType={handleSelectChartType} />
        <VStack space="stack.xxxs" flex={1}>
          <Box alignItems="center" justifyContent="flex-start" flex={1}>
            {chartComponent}
          </Box>
          <Flex
            flex="unset"
            alignItems={{ base: 'unset', sm: 'center', lg: 'unset' }}
            flexWrap="wrap"
            flexDirection="row"
            style={{
              columnGap: Utils.Token.ToInt(Spacing, 'stack.nano'),
              rowGap: Utils.Token.ToInt(Spacing, 'stack.xxxs'),
            }}>
            {chartItems}
          </Flex>
          {!!chartData.length && (
            <Button
              alignSelf="center"
              color="brand.tertiary.principal"
              variant="textLink"
              onPress={handleGoToFinancialDetailsWithCurrentTab}
              _text={{ fontFamily: Typography['fontfamily.base.semibold'] }}
              rightIcon={
                <Icon
                  name="ChevronRight"
                  size="xxxs"
                  testID="icon"
                  color="brand.tertiary.principal"
                />
              }>
              VER POSIÇÃO DETALHADA
            </Button>
          )}
        </VStack>
      </VStack>
    </Wrapper>
  );
};
